import { NumberField, SelectField } from '@flos/react-ui';
import type { OptionProps } from '@flos/react-ui/build/core/forms/select/select-input';
import type { OPCTermDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { isOpcDataFilledAtom } from '../../../../recoil/atoms/isOpcDataFilledAtom';

const prhManualSumIsValid = (insuranceSum: string, manualSum: string = ''): boolean => {
    if (isNaN(parseInt(insuranceSum))) {
        const sum = insuranceSum.split(' ')[1].replaceAll('.', '') ?? '';
        return parseInt(manualSum) > parseInt(sum);
    }
    return false;
};

const AdjustPrhHolidayInsuranceSum = ({
    termInfo,
    isAdjustableDataValid,
    updateTermStringValue,
    updateTermValue
}: {
    termInfo: OPCTermDTO;
    updateTermStringValue: (termValue: string) => void;
    updateTermValue: (termValue: number) => void;
    isAdjustableDataValid: boolean;
    overrideSubHeader?: string;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const options = termInfo.options?.map((option) => ({
        label: option.name,
        value: option.code
    })) as OptionProps[];
    const initialOption = options.find((option) => option.label === termInfo.value);
    const lastOption = options[options.length - 1];
    const [selectedOption, setSelectedOption] = useState(initialOption?.value);
    const [showManualSum, setShowManualSum] = useState(selectedOption === lastOption.value);
    const [manualSum, setManualSum] = useState<string>(termInfo.directValue?.toString() ?? '');
    const [isManualSumValid, setIsManualSumValid] = useState<boolean>(true);
    const setIsOpcDataFilled = useSetRecoilState(isOpcDataFilledAtom);

    const onInsuranceSumChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedIndex = event.target.selectedIndex;
        const { value } = event.target.options[selectedIndex];
        setShowManualSum(value === lastOption.value);
        setIsOpcDataFilled(showManualSum ? prhManualSumIsValid(lastOption.label, manualSum) : true);
        setSelectedOption(value);
        updateTermStringValue(value);
    };

    const onManualSumBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const number = event.target.value?.split(' ')[0].replaceAll('.', '');
        const isValidInput = prhManualSumIsValid(lastOption.label, number);
        setIsManualSumValid(isValidInput);
        setIsOpcDataFilled(isValidInput);
        if (isValidInput) {
            updateTermValue(parseInt(number));
        }
    };

    const onManualSumChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const number = event.target.value?.split(' ')[0].replaceAll('.', '');
        setManualSum(number);
    };

    return (
        <>
            <h6>{getHippoText('DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE-INPUT')}</h6>
            <SelectField
                label={getHippoText('DSS-OPC-INPUT-LABEL')}
                value={selectedOption}
                className="u-spacing-stack-none"
                options={options}
                placeholder={`${String(termInfo.value)} kr.`}
                onChange={onInsuranceSumChange}
                isValid={isAdjustableDataValid}
            />
            {showManualSum && (
                <NumberField
                    label={getHippoText('DSS-COVERAGE-PACKAGE-PAGE-MANUALSUM-INPUT')}
                    value={manualSum}
                    onBlur={onManualSumBlur}
                    onChange={onManualSumChange}
                    suffix=" kr."
                    withFormatting
                    isValid={isManualSumValid}
                    minLength={1}
                />
            )}
            {!isAdjustableDataValid && (
                <p className="paragraph--small u-color-supplement-error-base">
                    Du har valgt den samme selvrisiko, som du allerede har. Vælg en anden, eller
                    klik fortryd.
                </p>
            )}
        </>
    );
};

export default AdjustPrhHolidayInsuranceSum;
